import { createSelector, Selector } from 'reselect';
import { RootState } from '../root-reducer';
import type { ICampaignApplicationReviewData } from './campaignApplicationReview.types';
import type { ICampaignWorkspaceData } from './campaignWorkspace.types';
import type { ICommissionWalletSummary } from '../../types/global/commissionWallet.types';
import {
    IBuzzBridgeProfileResponse,
    IBrand,
    ICampaign,
    IHostDashboardAggregates,
    IHostDashboardData,
    IHostDashboardRecentCampaign,
    IInfluencer,
    IWalletDashboard,
    IWalletFundOptions,
    IWalletTransaction,
} from './types';

export const brandsSelector: Selector<RootState, IBrand[]> = createSelector(
    (state: RootState) => state.buzzbridge.brands,
    brands => brands,
);

export const activeBrandDashboardBrandSelector: Selector<RootState, IBrand | null> = createSelector(
    (state: RootState) => state.buzzbridge.activeBrandDashboardBrand,
    b => b,
);

export const gettingBrandsSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingBrands,
    v => v,
);

export const addingBrandSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.addingBrand,
    v => v,
);

export const updatingBrandSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.updatingBrand,
    v => v,
);

export const brandErrorSelector: Selector<RootState, unknown> = createSelector(
    (state: RootState) => state.buzzbridge.brandError,
    e => e,
);

export const connectingBrandSocialSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.connectingBrandSocial,
    v => v,
);

// Host dashboard selectors
export const hostDashboardSelector: Selector<RootState, IHostDashboardData | null> = createSelector(
    (state: RootState) => state.buzzbridge.hostDashboard,
    d => d,
);

export const gettingHostDashboardSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingHostDashboard,
    v => v,
);

export const hostDashboardErrorSelector: Selector<RootState, unknown> = createSelector(
    (state: RootState) => state.buzzbridge.hostDashboardError,
    e => e,
);

export const hostDashboardAggregatesSelector: Selector<RootState, IHostDashboardAggregates | null> = createSelector(
    hostDashboardSelector,
    d => d?.aggregates ?? null,
);

export const hostDashboardRecentCampaignsSelector: Selector<RootState, IHostDashboardRecentCampaign[]> = createSelector(
    hostDashboardSelector,
    d => d?.recent_campaigns ?? [],
);

// Campaign selectors
export const managedCampaignsSelector: Selector<RootState, ICampaign[]> = createSelector(
    (state: RootState) => state.buzzbridge.managedCampaigns,
    c => c ?? [],
);

export const gettingManagedCampaignsSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingManagedCampaigns,
    v => v,
);

export const managedCampaignsPaginationSelector: Selector<
    RootState,
    { currentPage: number; totalPages: number; totalCount: number }
> = createSelector(
    (state: RootState) => state.buzzbridge.managedCampaignsPagination,
    p => p ?? { currentPage: 1, totalPages: 1, totalCount: 0 },
);

export const creatingCampaignSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.creatingCampaign,
    v => v,
);

export const lastCreatedCampaignIdSelector: Selector<RootState, string | null> = createSelector(
    (state: RootState) => state.buzzbridge.lastCreatedCampaignId,
    v => v,
);

export const currentCampaignSelector: Selector<RootState, ICampaign | null> = createSelector(
    (state: RootState) => state.buzzbridge.currentCampaign,
    c => c,
);

export const gettingCampaignDetailsSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingCampaignDetails,
    v => v,
);

export const updatingCampaignSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.updatingCampaign,
    v => v,
);

export const updatingCampaignStatusSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.updatingCampaignStatus,
    v => v,
);

// Campaign Wallet selectors
export const walletDashboardSelector: Selector<RootState, IWalletDashboard | null> = createSelector(
    (state: RootState) => state.buzzbridge.walletDashboard,
    d => d,
);

export const gettingWalletDashboardSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingWalletDashboard,
    v => v,
);

export const commissionWalletSelector: Selector<RootState, ICommissionWalletSummary | null> = createSelector(
    (state: RootState) => state.buzzbridge.commissionWallet,
    w => w,
);

export const gettingCommissionWalletSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingCommissionWallet,
    v => v,
);

export const cashingOutCommissionWalletSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.cashingOutCommissionWallet,
    v => v,
);

export const walletFundOptionsSelector: Selector<RootState, IWalletFundOptions | null> = createSelector(
    (state: RootState) => state.buzzbridge.walletFundOptions,
    o => o,
);

export const gettingWalletFundOptionsSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingWalletFundOptions,
    v => v,
);

export const fundingWalletSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.fundingWallet,
    v => v,
);

export const walletTransactionsSelector: Selector<RootState, IWalletTransaction[]> = createSelector(
    (state: RootState) => state.buzzbridge.walletTransactions,
    t => t ?? [],
);

export const gettingWalletTransactionsSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingWalletTransactions,
    v => v,
);

export const adminCreditingCampaignWalletSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.adminCreditingCampaignWallet,
    v => Boolean(v),
);

export const adminCampaignWalletCreditResultSelector: Selector<
    RootState,
    import('./types').IAdminCreditWalletResult | null
> = createSelector(
    (state: RootState) => state.buzzbridge.adminCampaignWalletCreditResult,
    r => r,
);

export const requestingToJoinCampaignSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.requestingToJoin,
    v => v,
);

export const buzzBridgeProfileSelector: Selector<RootState, IBuzzBridgeProfileResponse | null | undefined> =
    createSelector(
        (state: RootState) => state.buzzbridge.buzzProfile,
        p => p,
    );

export const gettingBuzzBridgeProfileSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingBuzzProfile,
    v => Boolean(v),
);

export const noInfluencerProfileSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.noInfluencerProfile,
    v => Boolean(v),
);

export const publicInfluencerProfileSelector: Selector<RootState, IInfluencer | null> = createSelector(
    (state: RootState) => state.buzzbridge.publicInfluencerProfile,
    p => p,
);

export const publicInfluencerProfileUserNameSelector: Selector<RootState, string | null> = createSelector(
    (state: RootState) => state.buzzbridge.publicInfluencerProfileUserName,
    u => u,
);

export const gettingPublicInfluencerProfileSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingPublicInfluencerProfile,
    v => Boolean(v),
);

export const publicInfluencerProfileErrorSelector: Selector<RootState, unknown> = createSelector(
    (state: RootState) => state.buzzbridge.publicInfluencerProfileError,
    e => e,
);

export const campaignApplicationReviewSelector: Selector<RootState, ICampaignApplicationReviewData | null> =
    createSelector(
        (state: RootState) => state.buzzbridge.campaignApplicationReview,
        d => d,
    );

export const gettingCampaignApplicationReviewSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingCampaignApplicationReview,
    v => Boolean(v),
);

export const campaignApplicationReviewErrorSelector: Selector<RootState, unknown> = createSelector(
    (state: RootState) => state.buzzbridge.campaignApplicationReviewError,
    e => e,
);

export const campaignWorkspaceSelector: Selector<RootState, ICampaignWorkspaceData | null> = createSelector(
    (state: RootState) => state.buzzbridge.campaignWorkspace,
    w => w,
);

export const gettingCampaignWorkspaceSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingCampaignWorkspace,
    v => Boolean(v),
);

export const loadingOlderWorkspaceMessagesSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.loadingOlderWorkspaceMessages,
    v => Boolean(v),
);

export const campaignWorkspaceErrorSelector: Selector<RootState, unknown> = createSelector(
    (state: RootState) => state.buzzbridge.campaignWorkspaceError,
    e => e,
);

export const savingBrandReviewSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.savingBrandReview,
    v => Boolean(v),
);

export const brandReviewSaveErrorSelector: Selector<RootState, unknown> = createSelector(
    (state: RootState) => state.buzzbridge.brandReviewSaveError,
    e => e,
);

export const savingInfluencerReviewSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.savingInfluencerReview,
    v => Boolean(v),
);

export const influencerReviewSaveErrorSelector: Selector<RootState, unknown> = createSelector(
    (state: RootState) => state.buzzbridge.influencerReviewSaveError,
    e => e,
);

export const sendingCampaignThreadMessageSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.sendingCampaignThreadMessage,
    v => Boolean(v),
);

export const loadingOlderCampaignThreadMessagesSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.loadingOlderCampaignThreadMessages,
    v => Boolean(v),
);

export const campaignThreadMessageErrorSelector: Selector<RootState, unknown> = createSelector(
    (state: RootState) => state.buzzbridge.campaignThreadMessageError,
    e => e,
);

export const campaignThreadTypingSelector = (
    participantId: string,
): Selector<RootState, { userId: string; displayName?: string } | null> =>
    createSelector(
        (state: RootState) => state.buzzbridge.campaignThreadTyping?.[participantId] ?? null,
        typing => typing,
    );

export const submittingParticipantWorkSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.submittingParticipantWork,
    v => Boolean(v),
);

export const retryingSubmissionProcessingSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.retryingSubmissionProcessing,
    v => Boolean(v),
);

export const participantWorkSubmitErrorSelector: Selector<RootState, unknown> = createSelector(
    (state: RootState) => state.buzzbridge.participantWorkSubmitError,
    e => e,
);

export const participantUploadProgressSelector: Selector<RootState, number> = createSelector(
    (state: RootState) => state.buzzbridge.participantUploadProgress,
    v => v ?? 0,
);

export const submittingLiveUrlSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.submittingLiveUrl,
    v => Boolean(v),
);

export const markingSharedSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.markingShared,
    v => Boolean(v),
);

export const requestingLiveUrlRevisionSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.requestingLiveUrlRevision,
    v => Boolean(v),
);

export const withdrawingFromCampaignSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.withdrawingFromCampaign,
    v => Boolean(v),
);

export const requestingDeliveryExtensionSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.requestingDeliveryExtension,
    v => Boolean(v),
);

export const reviewingDeliveryExtensionSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.reviewingDeliveryExtension,
    v => Boolean(v),
);

export const openingLiveLinkWindowSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.openingLiveLinkWindow,
    v => Boolean(v),
);

export const payingOutSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.payingOut,
    v => Boolean(v),
);

export const payingOutRoundSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.payingOutRound,
    v => Boolean(v),
);

export const approvingRequestSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.approvingRequest,
    v => Boolean(v),
);

export const cancellingInviteSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.cancellingInvite,
    v => Boolean(v),
);

export const gettingPendingInvitesSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingPendingInvites,
    v => Boolean(v),
);

export const pendingInvitesListSelector: Selector<RootState, import('./types').ICampaignParticipantDetail[]> =
    createSelector(
        (state: RootState) => state.buzzbridge.pendingInvitesList,
        v => v ?? [],
    );

export const pendingInvitesPaginationSelector = createSelector(
    (state: RootState) => state.buzzbridge.pendingInvitesPagination,
    v => v,
);

export const bulkApprovingRequestsSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.bulkApprovingRequests,
    v => Boolean(v),
);

export const bulkRejectingRequestsSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.bulkRejectingRequests,
    v => Boolean(v),
);

export const bulkRescindingParticipantsSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.bulkRescindingParticipants,
    v => Boolean(v),
);

export const bulkRescindLastSkippedSelector = createSelector(
    (state: RootState) => state.buzzbridge.bulkRescindLastSkipped,
    (v): { id: string; reason: string }[] => v ?? [],
);

export const approvingDeliverableSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.approvingDeliverable,
    v => Boolean(v),
);

export const requestingDeliverableUpdateSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.requestingUpdate,
    v => Boolean(v),
);

export const bulkRequestingRevisionSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.bulkRequestingRevision,
    v => Boolean(v),
);

export const relaunchingCampaignSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.relaunchingCampaign,
    v => Boolean(v),
);

export const relaunchedCampaignIdSelector: Selector<RootState, string | null> = createSelector(
    (state: RootState) => state.buzzbridge.relaunchedCampaignId,
    v => v ?? null,
);

export const campaignReportSummarySelector: Selector<
    RootState,
    import('../../types/global/campaignReport.types').ICampaignReportSummary | null
> = createSelector(
    (state: RootState) => state.buzzbridge.campaignReportSummary,
    s => s,
);

export const campaignReportContentSelector: Selector<
    RootState,
    import('../../types/global/campaignReport.types').ICampaignReportContentResponse | null
> = createSelector(
    (state: RootState) => state.buzzbridge.campaignReportContent,
    c => c,
);

export const campaignReportCreatorsSelector: Selector<
    RootState,
    import('../../types/global/campaignReport.types').ICampaignReportCreatorsResponse | null
> = createSelector(
    (state: RootState) => state.buzzbridge.campaignReportCreators,
    c => c,
);

export const campaignReportSyncStatusSelector: Selector<
    RootState,
    import('../../types/global/campaignReport.types').ICampaignReportSyncStatusResponse | null
> = createSelector(
    (state: RootState) => state.buzzbridge.campaignReportSyncStatus,
    s => s,
);

export const gettingCampaignReportSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.gettingCampaignReport,
    v => Boolean(v),
);

export const syncingCampaignReportSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.syncingCampaignReport,
    v => Boolean(v),
);

export const boostingCampaignReportSelector: Selector<RootState, boolean> = createSelector(
    (state: RootState) => state.buzzbridge.boostingCampaignReport,
    v => Boolean(v),
);
